import type {
  DashboardSummary,
  ReleaseInfo,
  RepoReleaseSummary,
  TimelineEvent,
} from '@/api/dashboard';

const createRelease = (
  id: number,
  tagName: string,
  name: string,
  body: string,
  htmlUrl: string,
  publishedAt: string,
  author: string,
  prerelease = false,
): ReleaseInfo => ({
  id,
  tagName,
  name,
  body,
  htmlUrl,
  publishedAt,
  prerelease,
  draft: false,
  author,
});

const createRepoReleases = (
  repo: string,
  releases: ReleaseInfo[],
): RepoReleaseSummary => ({
  owner: 'HON-IA',
  repo,
  releases,
});

export const mockDashboardSummary: DashboardSummary = {
  metrics: {
    totalMilestones: 12,
    completedMilestones: 5,
    overallProgressPct: 58,
    activeInitiatives: 4,
    releasesThisQuarter: 8,
  },
  categorySummaries: [
    {
      category: 'Kubernetes Migration',
      total: 4,
      todo: 0,
      inProgress: 1,
      done: 3,
      blocked: 0,
    },
    {
      category: 'Observability',
      total: 3,
      todo: 0,
      inProgress: 2,
      done: 1,
      blocked: 0,
    },
    {
      category: 'Security & Compliance',
      total: 3,
      todo: 1,
      inProgress: 1,
      done: 1,
      blocked: 0,
    },
    {
      category: 'Developer Experience',
      total: 3,
      todo: 1,
      inProgress: 2,
      done: 0,
      blocked: 0,
    },
  ],
  topMilestones: [
    {
      id: 'milestone-aks-cluster-migration',
      title: 'AKS Cluster Migration',
      description:
        'Cut over shared platform workloads to production AKS clusters with zero-downtime traffic shifting.',
      category: 'Kubernetes Migration',
      status: 'in_progress',
      completionPct: 82,
      sortOrder: 1,
      createdAt: '2026-01-08T14:00:00Z',
      updatedAt: '2026-05-21T16:30:00Z',
    },
    {
      id: 'milestone-helm-chart-standardization',
      title: 'Helm Chart Standardization',
      description:
        'Normalize service deployment templates, values schemas, and release gates across platform repositories.',
      category: 'Kubernetes Migration',
      status: 'done',
      completionPct: 100,
      sortOrder: 2,
      createdAt: '2026-01-15T10:00:00Z',
      updatedAt: '2026-04-02T11:15:00Z',
    },
    {
      id: 'milestone-opentelemetry-integration',
      title: 'OpenTelemetry Integration',
      description:
        'Roll out distributed traces, golden signals, and standardized dashboards for customer-facing APIs.',
      category: 'Observability',
      status: 'in_progress',
      completionPct: 76,
      sortOrder: 3,
      createdAt: '2026-01-21T09:30:00Z',
      updatedAt: '2026-05-18T12:00:00Z',
    },
    {
      id: 'milestone-vault-secret-management',
      title: 'Vault Secret Management',
      description:
        'Replace static credentials with short-lived secrets for application and pipeline access paths.',
      category: 'Security & Compliance',
      status: 'in_progress',
      completionPct: 68,
      sortOrder: 4,
      createdAt: '2026-02-03T15:00:00Z',
      updatedAt: '2026-05-16T17:20:00Z',
    },
    {
      id: 'milestone-ci-cd-modernization',
      title: 'CI/CD Pipeline Modernization',
      description:
        'Consolidate reusable workflows, policy checks, and deployment promotion logic into shared templates.',
      category: 'Developer Experience',
      status: 'in_progress',
      completionPct: 61,
      sortOrder: 5,
      createdAt: '2026-02-10T08:00:00Z',
      updatedAt: '2026-05-12T18:45:00Z',
    },
    {
      id: 'milestone-cloud-sql-migration',
      title: 'Database Migration to Cloud SQL',
      description:
        'Move platform persistence services to managed Cloud SQL with resilient backup and failover coverage.',
      category: 'Kubernetes Migration',
      status: 'done',
      completionPct: 100,
      sortOrder: 6,
      createdAt: '2026-01-28T13:15:00Z',
      updatedAt: '2026-03-19T09:00:00Z',
    },
    {
      id: 'milestone-service-mesh',
      title: 'Service Mesh Implementation',
      description:
        'Enable secure east-west traffic controls, retry policies, and canary rollout governance.',
      category: 'Observability',
      status: 'todo',
      completionPct: 24,
      sortOrder: 7,
      createdAt: '2026-03-02T11:00:00Z',
      updatedAt: '2026-05-01T14:10:00Z',
    },
    {
      id: 'milestone-zero-trust-network',
      title: 'Zero Trust Network Policies',
      description:
        'Apply namespace-level segmentation and verified service identity rules across every production cluster.',
      category: 'Security & Compliance',
      status: 'todo',
      completionPct: 18,
      sortOrder: 8,
      createdAt: '2026-03-12T10:00:00Z',
      updatedAt: '2026-05-09T13:35:00Z',
    },
    {
      id: 'milestone-platform-golden-path',
      title: 'Platform Golden Path Portal',
      description:
        'Publish paved-road templates, scorecards, and onboarding accelerators for product teams.',
      category: 'Developer Experience',
      status: 'in_progress',
      completionPct: 54,
      sortOrder: 9,
      createdAt: '2026-02-18T16:00:00Z',
      updatedAt: '2026-05-22T09:10:00Z',
    },
  ],
  currentRoadmap: [
    {
      id: 'roadmap-q2-2026-platform-governance',
      title: 'Platform governance scorecards',
      description: 'Executive scorecards for deployment health, compliance drift, and roadmap delivery.',
      quarter: 2,
      year: 2026,
      status: 'in_progress',
      sortOrder: 1,
    },
    {
      id: 'roadmap-q2-2026-aks-completion',
      title: 'AKS migration completion',
      description: 'Retire the final legacy runtime footprint and complete production cutover readiness.',
      quarter: 2,
      year: 2026,
      status: 'in_progress',
      sortOrder: 2,
    },
    {
      id: 'roadmap-q2-2026-release-automation',
      title: 'Release automation controls',
      description: 'Automated approval evidence and change ticket synchronization for quarterly audits.',
      quarter: 2,
      year: 2026,
      status: 'todo',
      sortOrder: 3,
    },
    {
      id: 'roadmap-q3-2026-service-mesh',
      title: 'Service mesh production rollout',
      description: 'Expand mesh sidecars to all critical services with golden-path policies and observability.',
      quarter: 3,
      year: 2026,
      status: 'todo',
      sortOrder: 1,
    },
    {
      id: 'roadmap-q3-2026-zero-trust',
      title: 'Zero trust network enforcement',
      description: 'Codify segmentation guardrails and east-west authorization by workload identity.',
      quarter: 3,
      year: 2026,
      status: 'todo',
      sortOrder: 2,
    },
    {
      id: 'roadmap-q3-2026-dr-drills',
      title: 'Disaster recovery validation drills',
      description: 'Run game-day exercises across clusters, databases, and GitOps recovery paths.',
      quarter: 3,
      year: 2026,
      status: 'todo',
      sortOrder: 3,
    },
    {
      id: 'roadmap-q3-2026-dev-portal',
      title: 'Developer experience portal launch',
      description: 'Unified access to templates, standards, and release guidance for every delivery team.',
      quarter: 3,
      year: 2026,
      status: 'in_progress',
      sortOrder: 4,
    },
  ],
  repoReleases: [
    createRepoReleases('onering-api-services', [
      createRelease(
        20101,
        'v2.4.0',
        'API Services v2.4.0',
        'Adds dashboard summary endpoints, release notes enrichment, and milestone aggregation improvements.',
        'https://github.com/HON-IA/onering-api-services/releases/tag/v2.4.0',
        '2026-05-20T13:00:00Z',
        'jamie.chen',
      ),
      createRelease(
        20100,
        'v2.3.2',
        'API Services v2.3.2',
        'Stabilizes GitHub release harvesting and roadmap synchronization jobs.',
        'https://github.com/HON-IA/onering-api-services/releases/tag/v2.3.2',
        '2026-04-24T10:30:00Z',
        'jamie.chen',
      ),
      createRelease(
        20092,
        'v2.3.0',
        'API Services v2.3.0',
        'Introduces milestone scoring and category summary projections.',
        'https://github.com/HON-IA/onering-api-services/releases/tag/v2.3.0',
        '2026-03-18T09:00:00Z',
        'alex.morgan',
      ),
    ]),
    createRepoReleases('onering-pipelines', [
      createRelease(
        20214,
        'v1.9.1',
        'Pipelines v1.9.1',
        'Improves reusable workflows, policy evidence capture, and deployment promotion visibility.',
        'https://github.com/HON-IA/onering-pipelines/releases/tag/v1.9.1',
        '2026-05-14T15:20:00Z',
        'noah.clark',
      ),
      createRelease(
        20208,
        'v1.9.0',
        'Pipelines v1.9.0',
        'Adds standardized release annotations and quality gates for shared services.',
        'https://github.com/HON-IA/onering-pipelines/releases/tag/v1.9.0',
        '2026-04-10T11:00:00Z',
        'noah.clark',
      ),
      createRelease(
        20201,
        'v1.8.5',
        'Pipelines v1.8.5',
        'Hardens build cache handling and artifact provenance reporting.',
        'https://github.com/HON-IA/onering-pipelines/releases/tag/v1.8.5',
        '2026-02-27T08:45:00Z',
        'priya.shah',
      ),
    ]),
    createRepoReleases('onering-infra', [
      createRelease(
        20311,
        'v3.2.0',
        'Infrastructure v3.2.0',
        'Provisioning updates for multi-cluster AKS landing zones and baseline observability resources.',
        'https://github.com/HON-IA/onering-infra/releases/tag/v3.2.0',
        '2026-05-08T14:05:00Z',
        'samuel.lee',
      ),
      createRelease(
        20303,
        'v3.1.4',
        'Infrastructure v3.1.4',
        'Refines network policy defaults and Cloud SQL backup policy automation.',
        'https://github.com/HON-IA/onering-infra/releases/tag/v3.1.4',
        '2026-04-03T16:30:00Z',
        'samuel.lee',
      ),
      createRelease(
        20298,
        'v3.1.0',
        'Infrastructure v3.1.0',
        'Adds managed identity integrations for secret delivery and GitOps bootstrap.',
        'https://github.com/HON-IA/onering-infra/releases/tag/v3.1.0',
        '2026-01-30T12:10:00Z',
        'jamie.chen',
      ),
    ]),
    createRepoReleases('onering-gitops', [
      createRelease(
        20407,
        'v2.1.0',
        'GitOps v2.1.0',
        'Expands progressive delivery templates and automated drift remediation notifications.',
        'https://github.com/HON-IA/onering-gitops/releases/tag/v2.1.0',
        '2026-05-05T09:40:00Z',
        'maria.lopez',
      ),
      createRelease(
        20402,
        'v2.0.3',
        'GitOps v2.0.3',
        'Improves Argo CD project isolation and rollout visibility for platform squads.',
        'https://github.com/HON-IA/onering-gitops/releases/tag/v2.0.3',
        '2026-04-15T10:05:00Z',
        'maria.lopez',
      ),
    ]),
    createRepoReleases('onering-helm-charts', [
      createRelease(
        20504,
        'v1.6.2',
        'Helm Charts v1.6.2',
        'Publishes standardized sidecar, ingress, and autoscaling templates for platform services.',
        'https://github.com/HON-IA/onering-helm-charts/releases/tag/v1.6.2',
        '2026-04-28T13:50:00Z',
        'alex.morgan',
      ),
      createRelease(
        20495,
        'v1.6.0',
        'Helm Charts v1.6.0',
        'Introduces opinionated chart scaffolds for API, worker, and cron workloads.',
        'https://github.com/HON-IA/onering-helm-charts/releases/tag/v1.6.0',
        '2026-03-11T09:25:00Z',
        'alex.morgan',
      ),
    ]),
    createRepoReleases('onering-database', [
      createRelease(
        20602,
        'v1.4.1',
        'Database v1.4.1',
        'Delivers Cloud SQL failover tuning, migration observability, and backup verification hooks.',
        'https://github.com/HON-IA/onering-database/releases/tag/v1.4.1',
        '2026-05-02T17:15:00Z',
        'priya.shah',
      ),
      createRelease(
        20598,
        'v1.4.0',
        'Database v1.4.0',
        'Adds migration readiness checks and rollback automation for schema deployments.',
        'https://github.com/HON-IA/onering-database/releases/tag/v1.4.0',
        '2026-04-21T08:20:00Z',
        'priya.shah',
      ),
      createRelease(
        20587,
        'v1.3.5',
        'Database v1.3.5',
        'Stabilizes migration orchestration across shared platform environments.',
        'https://github.com/HON-IA/onering-database/releases/tag/v1.3.5',
        '2026-02-14T12:40:00Z',
        'samuel.lee',
      ),
    ]),
  ],
  trends: {
    months: 12,
    points: [
      {
        month: '2025-06',
        label: 'Jun',
        milestonesCompleted: 0,
        cumulativeCompleted: 0,
        releases: 1,
      },
      {
        month: '2025-07',
        label: 'Jul',
        milestonesCompleted: 1,
        cumulativeCompleted: 1,
        releases: 0,
      },
      {
        month: '2025-08',
        label: 'Aug',
        milestonesCompleted: 0,
        cumulativeCompleted: 1,
        releases: 2,
      },
      {
        month: '2025-09',
        label: 'Sep',
        milestonesCompleted: 0,
        cumulativeCompleted: 1,
        releases: 1,
      },
      {
        month: '2025-10',
        label: 'Oct',
        milestonesCompleted: 1,
        cumulativeCompleted: 2,
        releases: 0,
      },
      {
        month: '2025-11',
        label: 'Nov',
        milestonesCompleted: 0,
        cumulativeCompleted: 2,
        releases: 3,
      },
      {
        month: '2025-12',
        label: 'Dec',
        milestonesCompleted: 0,
        cumulativeCompleted: 2,
        releases: 0,
      },
      {
        month: '2026-01',
        label: 'Jan',
        milestonesCompleted: 0,
        cumulativeCompleted: 2,
        releases: 1,
      },
      {
        month: '2026-02',
        label: 'Feb',
        milestonesCompleted: 0,
        cumulativeCompleted: 2,
        releases: 2,
      },
      {
        month: '2026-03',
        label: 'Mar',
        milestonesCompleted: 1,
        cumulativeCompleted: 3,
        releases: 2,
      },
      {
        month: '2026-04',
        label: 'Apr',
        milestonesCompleted: 1,
        cumulativeCompleted: 4,
        releases: 6,
      },
      {
        month: '2026-05',
        label: 'May',
        milestonesCompleted: 1,
        cumulativeCompleted: 5,
        releases: 5,
      },
    ],
    statusDistribution: {
      todo: 2,
      inProgress: 6,
      done: 5,
      blocked: 0,
      total: 13,
    },
  },
};

export const mockDashboardTimeline: TimelineEvent[] = [
  {
    date: '2026-05-22T09:15:00Z',
    type: 'roadmap_change',
    title: 'Q3 developer portal launch moved to active delivery',
    description: 'Leadership approved acceleration of the developer experience portal to support golden-path onboarding.',
    metadata: { quarter: 3, year: 2026, initiative: 'Developer Experience' },
  },
  {
    date: '2026-05-20T13:00:00Z',
    type: 'release',
    title: 'onering-api-services v2.4.0 released',
    description: 'Dashboard summary endpoints and enriched release metadata shipped to production.',
    metadata: { repo: 'onering-api-services', tagName: 'v2.4.0' },
  },
  {
    date: '2026-05-18T12:00:00Z',
    type: 'milestone_completed',
    title: 'OpenTelemetry dashboards expanded to critical services',
    description: 'Golden signal dashboards now cover API, worker, and messaging paths.',
    metadata: { milestoneId: 'milestone-opentelemetry-integration', completionPct: 76 },
  },
  {
    date: '2026-05-14T15:20:00Z',
    type: 'release',
    title: 'onering-pipelines v1.9.1 released',
    description: 'Reusable workflow improvements and deployment promotion evidence went live.',
    metadata: { repo: 'onering-pipelines', tagName: 'v1.9.1' },
  },
  {
    date: '2026-05-12T18:45:00Z',
    type: 'roadmap_change',
    title: 'CI/CD modernization moved to delivery assurance review',
    description: 'The platform steering team approved the final rollout wave for shared templates.',
    metadata: { initiative: 'Developer Experience', status: 'in_progress' },
  },
  {
    date: '2026-05-08T14:05:00Z',
    type: 'release',
    title: 'onering-infra v3.2.0 released',
    description: 'Multi-cluster AKS landing zone updates and observability baselines were published.',
    metadata: { repo: 'onering-infra', tagName: 'v3.2.0' },
  },
  {
    date: '2026-05-05T09:40:00Z',
    type: 'release',
    title: 'onering-gitops v2.1.0 released',
    description: 'Progressive delivery templates and drift notifications expanded to shared services.',
    metadata: { repo: 'onering-gitops', tagName: 'v2.1.0' },
  },
  {
    date: '2026-05-02T17:15:00Z',
    type: 'release',
    title: 'onering-database v1.4.1 released',
    description: 'Cloud SQL failover tuning and migration backup verification hooks shipped.',
    metadata: { repo: 'onering-database', tagName: 'v1.4.1' },
  },
  {
    date: '2026-04-28T13:50:00Z',
    type: 'release',
    title: 'onering-helm-charts v1.6.2 released',
    description: 'Standardized sidecar, ingress, and autoscaling templates were published.',
    metadata: { repo: 'onering-helm-charts', tagName: 'v1.6.2' },
  },
  {
    date: '2026-04-24T10:30:00Z',
    type: 'milestone_completed',
    title: 'Helm chart standardization completed',
    description: 'All platform services now deploy from a consistent chart baseline and values schema.',
    metadata: { milestoneId: 'milestone-helm-chart-standardization', completionPct: 100 },
  },
  {
    date: '2026-04-21T08:20:00Z',
    type: 'release',
    title: 'onering-database v1.4.0 released',
    description: 'Migration readiness checks and rollback automation were introduced.',
    metadata: { repo: 'onering-database', tagName: 'v1.4.0' },
  },
  {
    date: '2026-04-15T10:05:00Z',
    type: 'release',
    title: 'onering-gitops v2.0.3 released',
    description: 'Argo CD project isolation and rollout visibility improvements shipped.',
    metadata: { repo: 'onering-gitops', tagName: 'v2.0.3' },
  },
  {
    date: '2026-04-10T11:00:00Z',
    type: 'release',
    title: 'onering-pipelines v1.9.0 released',
    description: 'Shared release annotations and quality gates became available to delivery teams.',
    metadata: { repo: 'onering-pipelines', tagName: 'v1.9.0' },
  },
  {
    date: '2026-04-03T16:30:00Z',
    type: 'release',
    title: 'onering-infra v3.1.4 released',
    description: 'Network policy defaults and Cloud SQL backup automation were updated.',
    metadata: { repo: 'onering-infra', tagName: 'v3.1.4' },
  },
  {
    date: '2026-03-19T09:00:00Z',
    type: 'milestone_completed',
    title: 'Cloud SQL migration completed',
    description: 'Managed database cutover completed with backup and failover readiness verified.',
    metadata: { milestoneId: 'milestone-cloud-sql-migration', completionPct: 100 },
  },
  {
    date: '2026-03-18T09:00:00Z',
    type: 'release',
    title: 'onering-api-services v2.3.0 released',
    description: 'Milestone scoring and category summary projections launched.',
    metadata: { repo: 'onering-api-services', tagName: 'v2.3.0' },
  },
  {
    date: '2026-03-11T09:25:00Z',
    type: 'release',
    title: 'onering-helm-charts v1.6.0 released',
    description: 'Opinionated API, worker, and cron scaffolds were standardized for new services.',
    metadata: { repo: 'onering-helm-charts', tagName: 'v1.6.0' },
  },
  {
    date: '2026-02-27T08:45:00Z',
    type: 'release',
    title: 'onering-pipelines v1.8.5 released',
    description: 'Artifact provenance reporting and build cache hardening were delivered.',
    metadata: { repo: 'onering-pipelines', tagName: 'v1.8.5' },
  },
  {
    date: '2026-02-14T12:40:00Z',
    type: 'roadmap_change',
    title: 'Security automation workstream added to Q2 checkpoints',
    description: 'Quarterly roadmap updated to include automated approval evidence for release governance.',
    metadata: { quarter: 2, year: 2026, initiative: 'Security & Compliance' },
  },
  {
    date: '2026-01-30T12:10:00Z',
    type: 'release',
    title: 'onering-infra v3.1.0 released',
    description: 'Managed identity integrations for secret delivery and GitOps bootstrap shipped.',
    metadata: { repo: 'onering-infra', tagName: 'v3.1.0' },
  },
];
